import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  InputSignal,
  NgZone,
  OnInit,
  Signal,
  TemplateRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard } from '@angular/material/card';
import { Observable, throttleTime } from 'rxjs';
import { concatMap, filter, map, mergeMap, pairwise, take } from 'rxjs/operators';

import { UtilPipesModule } from '@core/shared/util';

import { SpinnerComponent } from '../spinner/spinner.component';

import { PagedDataSource } from './paged-data-source';
import { runInZone } from './run-in-zone';

@Component({
  selector: 'mp-infinite-scroll',
  templateUrl: './infinite-scroll.component.html',
  styleUrl: './infinite-scroll.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgTemplateOutlet, ScrollingModule, MatCard, SpinnerComponent, UtilPipesModule],
})
export class InfiniteScrollComponent<T, TParams extends object | undefined> implements OnInit {
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly viewport: Signal<CdkVirtualScrollViewport> = viewChild.required(CdkVirtualScrollViewport);

  readonly dataSource: InputSignal<PagedDataSource<T, TParams>> = input.required<PagedDataSource<T, TParams>>();

  /**
   * Height of an individual list-item (in pixels).
   *
   * Initially set to 80px since this is the default template's height.
   * **Needs to be set!**
   */
  readonly itemHeight: InputSignal<number> = input<number>(80);

  readonly updateInterval: InputSignal<number> = input<number>(300);

  readonly scrollThresholdShare: InputSignal<number> = input<number>(0);

  // Optional templates
  readonly itemTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();
  readonly loadingFooterTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();
  readonly loadMoreFooterTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();
  readonly noMoreFooterTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();

  get scrollThreshold(): number {
    return this.viewport().getViewportSize() * this.scrollThresholdShare();
  }

  scrollTo(pos: number, from: 'top' | 'bottom' = 'top', behavior: 'auto' | 'smooth' = 'smooth'): void {
    this.viewport().scrollTo({ [from]: pos, behavior });
  }

  scrollToTop(behavior: 'auto' | 'smooth' = 'smooth'): void {
    this.scrollTo(0, 'top', behavior);
  }

  scrollToIndex(itemIndex: number, behavior: 'auto' | 'smooth' = 'smooth'): void {
    this.viewport().scrollToIndex(itemIndex, behavior);
  }

  ngOnInit(): void {
    this.viewport()
      .elementScrolled()
      .pipe(
        map(() => this.viewport().measureScrollOffset('bottom')),
        pairwise(),
        filter(([lastY, currentY]) => this.wasScrolledIntoThreshold(lastY, currentY)),
        throttleTime(this.updateInterval()),
        concatMap(() => this.fetchDataIfNeeded()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.dataSource()
      .queryParams$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.scrollToTop('auto') });
  }

  private wasScrolledIntoThreshold(lastY: number, currentY: number): boolean {
    const isDownwardScroll: boolean = currentY < lastY;
    const isWithinThreshold: boolean = currentY <= this.scrollThreshold;
    return isDownwardScroll && isWithinThreshold;
  }

  private fetchDataIfNeeded(): Observable<unknown> {
    return this.dataSource().hasMore$.pipe(
      take(1),
      runInZone(this.ngZone),
      filter((hasMore) => hasMore),
      mergeMap(() => this.dataSource().fetchMore()),
    );
  }
}
